import type { RoomOffsetHierarchy } from 'lumen/models/recommendation-strategy';

export function findPrimaryPath(
  hierarchy: Record<string, RoomOffsetHierarchy>,
  leadingRoomCode: string | null,
): string[] {
  let bestPath: string[] = [];

  for (const [roomCode, node] of Object.entries(hierarchy)) {
    const childPath = findPrimaryPath(node.children, leadingRoomCode);
    const path = [roomCode, ...childPath];

    if (
      path.length > bestPath.length ||
      (path.length === bestPath.length &&
        leadingRoomCode !== null &&
        path.includes(leadingRoomCode))
    ) {
      bestPath = path;
    }
  }

  return bestPath;
}

// Offset fields treat an empty input as 0 — both for display and when persisting to the strategy.
export function resolveOffsetValue(value: number | null): number {
  return value ?? 0;
}

/*
 * Flags every room whose maximum offset is below its minimum, including a
 * missing/null maximum_offset (legacy data predating the field, resolved
 * to 0 the same way the UI already does). Root nodes are never flagged:
 * RoomItem never renders an offset UI for a room without a parent, so a
 * flagged root would permanently block Save with no visible way to fix it.
 */
export function getInvalidOffsetRoomCodes(
  hierarchy: Record<string, RoomOffsetHierarchy>,
  isRoot = true,
): string[] {
  const invalidRoomCodes: string[] = [];

  for (const [roomCode, node] of Object.entries(hierarchy)) {
    if (!isRoot) {
      const minOffset = resolveOffsetValue(node.offset.value);
      const maxOffset = resolveOffsetValue(node.maximum_offset?.value ?? null);

      if (maxOffset < minOffset) {
        invalidRoomCodes.push(roomCode);
      }
    }

    invalidRoomCodes.push(...getInvalidOffsetRoomCodes(node.children, false));
  }

  return invalidRoomCodes;
}

type HierarchyTransform = (
  roomCode: string,
  node: RoomOffsetHierarchy,
) =>
  | void
  | { action: 'remove' }
  | { action: 'update'; node: RoomOffsetHierarchy; roomCode: string }
  | {
      action: 'add-child';
      childCode: string;
      isMinMaxOffsetEnabled: boolean;
      maxOffsetValue: number;
      minOffsetValue: number;
    };

/**
 * Recursively walks a room-offset hierarchy tree and applies `transform` to
 * every node. The transform controls what happens at each node:
 *
 * - `void / undefined` — leave this node unchanged and recurse into its children
 * - `{ action: 'remove' }` — delete this node (children are dropped with it)
 * - `{ action: 'update', roomCode, node }` — replace this node's key and/or value;
 *   recursion stops here so the caller controls the replacement subtree
 * - `{ action: 'add-child', childCode, minOffsetValue, maxOffsetValue }` — append a new leaf child
 *   under this node with the user-supplied offsets; existing children are preserved
 *
 * Because room codes are unique across the tree, a transform typically matches
 * one node and lets all others fall through implicitly.
 */
export function updateHierarchy(
  hierarchy: Record<string, RoomOffsetHierarchy>,
  transform: HierarchyTransform,
): Record<string, RoomOffsetHierarchy> {
  const result: typeof hierarchy = {};

  for (const [roomCode, node] of Object.entries(hierarchy)) {
    const outcome = transform(roomCode, node);

    if (!outcome) {
      // no changes for this room, keep its children and offset
      result[roomCode] = {
        ...node,
        children: updateHierarchy(node.children, transform),
      };
    } else if (outcome.action === 'remove') {
      continue; // skips adding the room
    } else if (outcome.action === 'add-child') {
      result[roomCode] = {
        ...node,
        children: {
          ...node.children,
          [outcome.childCode]: {
            children: {},
            offset: {
              type: 'absolute',
              value: outcome.minOffsetValue,
            },
            ...(outcome.isMinMaxOffsetEnabled && {
              maximum_offset: {
                type: 'absolute',
                value: outcome.maxOffsetValue,
              },
            }),
          },
        },
      };
    } else if (outcome.action === 'update') {
      result[outcome.roomCode] = outcome.node;
    }
  }

  return result;
}
